import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { getDocuments, uploadDocument, deleteDocument, getEmployees, createDocument, verifyDocument } from '../api';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { maybeCompressImage, fmtBytes, MAX_UPLOAD_BYTES } from '../utils/compressImage';
import { FolderOpen, Plus, FileText, Download, Upload, Trash2, Eye, Search, Filter, RefreshCw, FileImage, File, FileSpreadsheet, FileType, CheckCircle, XCircle, Clock, ShieldCheck, ChevronsUpDown, Check, Database, HardDrive } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import api from '../api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentManagement = ({ user, setUser }) => {
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Verification dialog state
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [documentToVerify, setDocumentToVerify] = useState(null);
  const [verifyRemarks, setVerifyRemarks] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // Re-upload dialog state
  const [reuploadDialogOpen, setReuploadDialogOpen] = useState(false);
  const [documentToReupload, setDocumentToReupload] = useState(null);
  const [reuploadFile, setReuploadFile] = useState(null);
  const reuploadFileInputRef = useRef(null);
  
  // Filter states
  const [filterVerificationStatus, setFilterVerificationStatus] = useState('all');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDocType, setFilterDocType] = useState('all');
  const [filterEmployeeId, setFilterEmployeeId] = useState('all');
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  
  // Employee combobox state for upload dialog
  const [employeeComboOpen, setEmployeeComboOpen] = useState(false);
  
  // Storage quota for admins. Loaded async — page renders without it.
  const [storageUsage, setStorageUsage] = useState(null);

  const [formData, setFormData] = useState({
    employee_id: '',
    document_name: '',
    document_type: 'aadhar',
    document_url: '',
    file: null
  });
  const fileInputRef = useRef(null);

  const documentTypes = [
    { value: 'aadhar', label: 'Aadhar Card' },
    { value: 'pan', label: 'PAN Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'voter_id', label: 'Voter ID' },
    { value: 'resume', label: 'Resume/CV' },
    { value: 'education', label: 'Education Certificate' },
    { value: 'experience', label: 'Experience Letter' },
    { value: 'offer_letter', label: 'Offer Letter' },
    { value: 'relieving_letter', label: 'Relieving Letter' },
    { value: 'bank_statement', label: 'Bank Statement' },
    { value: 'other', label: 'Other Document' }
  ];

  const hasDocOverviewAccess = useSectionAccess('/documents-consolidated');

  useEffect(() => {
    if (hasDocOverviewAccess === null) return; // wait for permission check
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDocOverviewAccess]);

  const fetchData = async () => {
    try {
      const [docsRes, empRes] = await Promise.all([
        getDocuments(),
        getEmployees()
      ]);
      
      const allEmployees = empRes.data;
      setEmployees(allEmployees);
      
      // Find current employee record (used in both employee + permission-granted paths)
      const myEmployee = allEmployees.find(e => e.user_id === user?.id);
      setCurrentEmployee(myEmployee);
      
      // Employees with no explicit Doc Overview grant → only see own docs
      const isPrivileged = ['admin', 'hr_manager', 'hr_executive'].includes(user?.role) || hasDocOverviewAccess;
      
      if (!isPrivileged) {
        // Filter to show only own documents
        const myDocuments = docsRes.data.filter(d => d.employee_id === myEmployee?.id);
        setDocuments(myDocuments);
        
        // Set employee_id for the form (forced to self)
        if (myEmployee) {
          setFormData(prev => ({ ...prev, employee_id: myEmployee.id }));
        }
      } else {
        setDocuments(docsRes.data);
      }
      // Storage card refreshes on every list reload — keeps numbers fresh
      // after uploads/deletes without an extra effect.
      fetchStorageUsage();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  /* Pull aggregated storage usage (admin/HR only) — silent on failure.
     Folded into the existing fetchData lifecycle to avoid an extra effect. */
  const fetchStorageUsage = async () => {
    const isPrivileged = ['admin', 'hr_manager', 'hr_executive'].includes(user?.role) || hasDocOverviewAccess;
    if (!isPrivileged) return;
    try {
      const res = await api.get('/documents/admin/storage-usage', { timeout: 20000 });
      setStorageUsage(res.data);
    } catch (e) {
      /* card just won't render */
    }
  };

  const handleFileChange = async (e) => {
    const original = e.target.files[0];
    if (!original) return;
    // Always show "Compressing…" feedback even if the file is small — the
    // call returns instantly for non-image / below-threshold files.
    const compressing = original.type.startsWith('image/') && original.size >= 500 * 1024;
    if (compressing) {
      toast.loading(`Compressing ${fmtBytes(original.size)} image…`, { id: 'compress' });
    }
    let file = original;
    try {
      file = await maybeCompressImage(original);
    } catch {
      file = original; // safety net — never block upload on compression failure
    }
    if (compressing) {
      toast.dismiss('compress');
      if (file !== original && file.size < original.size) {
        const savedPct = Math.round((1 - file.size / original.size) * 100);
        toast.success(
          `Compressed ${fmtBytes(original.size)} → ${fmtBytes(file.size)} (saved ${savedPct}%)`,
          { duration: 4000 }
        );
      }
    }
    // Hard limit AFTER compression. If the user uploads a 12 MB image we
    // probably compressed it down to ~500 KB and this never triggers — but a
    // 10 MB image-based PDF can't be compressed in-browser, so it stays 10
    // MB and gets rejected here. Same number is enforced on the backend.
    if (file.size > MAX_UPLOAD_BYTES) {
      e.target.value = '';
      toast.error(
        `File is ${fmtBytes(file.size)} — over the ${fmtBytes(MAX_UPLOAD_BYTES)} limit. ` +
        `Try compressing the PDF (e.g. Smallpdf, Adobe) and re-upload.`,
        { duration: 8000, id: 'file-too-large' }
      );
      setFormData(prev => ({ ...prev, file: null, _compressionInfo: null }));
      return;
    }
    const fakeUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      file,
      document_url: fakeUrl,
      document_name: prev.document_name || original.name.split('.')[0],
      // Surface the pair for the upload dialog UI ("Original 3.2 MB → 480 KB")
      _compressionInfo: file === original
        ? { original_bytes: original.size, compressed_bytes: original.size, did_compress: false }
        : { original_bytes: original.size, compressed_bytes: file.size, did_compress: true },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      if (formData.file) {
        // Use the file upload endpoint for actual file uploads
        await uploadDocument(
          formData.file,
          formData.employee_id,
          formData.document_name,
          formData.document_type
        );
      } else if (formData.document_url) {
        // Use URL-based document creation for external links
        const docData = {
          employee_id: formData.employee_id,
          document_name: formData.document_name,
          document_type: formData.document_type,
          document_url: formData.document_url
        };
        await createDocument(docData);
      } else {
        toast.error('Please upload a file or enter a document URL');
        setUploading(false);
        return;
      }
      
      toast.success('Document uploaded successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        employee_id: !canManageAllDocs && currentEmployee ? currentEmployee.id : '',
        document_name: '',
        document_type: 'aadhar',
        document_url: '',
        file: null,
        _compressionInfo: null,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      await deleteDocument(documentToDelete.id);
      toast.success('Document deleted successfully');
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // GridFS migration handler — moves legacy base64-in-one-doc files into the
  // chunked GridFS bucket so large files stop hitting Cloudflare's 100s
  // gateway timeout. Production has 1600+ docs, so we batch in groups of 25
  // and loop until `remaining == 0`. Single-call backend processing of all
  // 1656 docs would exceed Cloudflare's gateway timeout (100s).
  const runMigration = async (applyChanges) => {
    if (!applyChanges) {
      const toastId = toast.loading('Scanning legacy documents…');
      try {
        const res = await api.post('/documents/admin/migrate-to-gridfs?limit=5', null, { timeout: 30000 });
        toast.dismiss(toastId);
        const d = res.data;
        const pending = d.has_pending ? (d.total_legacy_rows || 0) : 0;
        if (pending) {
          toast.info(`${pending} legacy documents detected.`, { duration: 4000 });
        } else {
          toast.success('No legacy documents to migrate.', { duration: 4000 });
        }
        return { ...d, remaining_before: pending, total_legacy_rows: d.total_legacy_rows };
      } catch (e) {
        toast.dismiss(toastId);
        toast.error(`Scan failed: ${e.response?.data?.detail || e.message}`);
        return null;
      }
    }

    // APPLY mode — loop one batch at a time, updating the toast with progress.
    // Each batch has built-in auto-retry on transient network errors so the
    // user doesn't have to keep clicking the button when Cloudflare or
    // axios trims a connection mid-upload. Already-migrated rows are
    // skipped automatically by the backend, so retries are idempotent.
    const toastId = toast.loading('Migrating documents to GridFS…');
    let totalMigrated = 0;
    let totalSkippedPresent = 0;
    let totalSkippedBad = 0;
    let totalLegacy = 0;
    let batchNum = 0;
    let lastResp = null;
    try {
      while (batchNum < 500) {
        batchNum++;
        let attempt = 0;
        let d = null;
        // Up to 3 attempts per batch with linear backoff. Most transient
        // failures (Cloudflare cuts, MongoDB lag spikes) clear within 5s.
        while (attempt < 3) {
          attempt++;
          try {
            toast.loading(
              attempt > 1
                ? `Retrying batch ${batchNum} (attempt ${attempt}/3)…`
                : `Migrating batch ${batchNum}…`,
              { id: toastId }
            );
            const res = await api.post(
              '/documents/admin/migrate-to-gridfs?apply=true&limit=1',
              null,
              { timeout: 90000 }
            );
            d = res.data;
            break;
          } catch (err) {
            // Decide whether to retry. 5xx, timeout, and network errors are
            // retryable; 4xx errors mean permission/validation problems and
            // should bubble up immediately. Cloudflare 520-524 (origin worker
            // killed or timeout) gets EXTRA backoff because the pod needs
            // time to restart on Kubernetes.
            const code = err.response?.status;
            const isCloudflareEdge = code >= 520 && code <= 526;
            const isRetryable =
              !err.response || (code >= 500 && code < 600) || err.code === 'ECONNABORTED';
            if (!isRetryable || attempt >= 3) throw err;
            // 15s for CF edge errors (worker reboot), 3s × attempt for others
            await new Promise(r => setTimeout(r, isCloudflareEdge ? 15000 : 3000 * attempt));
          }
        }

        lastResp = d;
        if (batchNum === 1) totalLegacy = d.total_legacy_rows || 0;
        totalMigrated += d.migrated || 0;
        totalSkippedPresent += d.skipped_already_in_gridfs || 0;
        totalSkippedBad += d.skipped_bad_data || 0;

        const processed = totalMigrated + totalSkippedPresent + totalSkippedBad;
        const pct = totalLegacy ? Math.round((processed / totalLegacy) * 100) : 0;
        toast.loading(
          `Migrating: ${processed}/${totalLegacy} (${pct}%) — batch ${batchNum}`,
          { id: toastId }
        );

        if ((d.batch_processed ?? 0) === 0) break;
        if ((d.remaining ?? 0) === 0) break;
      }
      toast.dismiss(toastId);
      toast.success(
        `Migration complete: ${totalMigrated} files moved, ` +
        `${totalSkippedPresent} already done, ${totalSkippedBad} bad. ` +
        `Re-open the failing document URL now — it should load instantly.`,
        { duration: 12000 }
      );
      return {
        migrated: totalMigrated,
        skipped_already_in_gridfs: totalSkippedPresent,
        skipped_bad_data: totalSkippedBad,
        batches: batchNum,
        total_legacy_rows: totalLegacy,
        remaining: lastResp?.remaining ?? 0,
      };
    } catch (e) {
      toast.dismiss(toastId);
      const detail = e.response?.data?.detail || e.message || 'unknown error';
      const partial = totalMigrated > 0
        ? ` Partial progress: ${totalMigrated} migrated across ${batchNum} batches — click again to resume.`
        : '';
      toast.error(`Migration failed at batch ${batchNum}: ${detail}.${partial}`, { duration: 10000 });
      return null;
    }
  };

  // === Free Up Space ===
  // After migration, every legacy row STILL holds the original 13MB base64
  // blob as a backup. This loop verifies each row's GridFS copy is readable
  // (1KB sample read) and then `$unset`s only the `data` field — the rest
  // of the metadata row stays for audit. NO files are deleted, only the
  // duplicate storage. Same batching/retry strategy as runMigration.
  const runFreeUpSpace = async (applyChanges) => {
    if (!applyChanges) {
      const toastId = toast.loading('Calculating reclaimable space…');
      try {
        const res = await api.post('/documents/admin/free-up-space', null, { timeout: 30000 });
        toast.dismiss(toastId);
        return res.data;
      } catch (e) {
        toast.dismiss(toastId);
        toast.error(`Scan failed: ${e.response?.data?.detail || e.message}`);
        return null;
      }
    }

    const toastId = toast.loading('Freeing up storage…');
    let totalFreed = 0;
    let totalPurged = 0;
    let totalFailed = 0;
    let totalFreeable = 0;
    let batchNum = 0;
    let allFailures = [];
    try {
      while (batchNum < 500) {
        batchNum++;
        let attempt = 0;
        let d = null;
        while (attempt < 3) {
          attempt++;
          try {
            toast.loading(
              attempt > 1
                ? `Retrying batch ${batchNum} (attempt ${attempt}/3)…`
                : `Freeing space — batch ${batchNum}…`,
              { id: toastId }
            );
            const res = await api.post(
              '/documents/admin/free-up-space?apply=true&limit=3',
              null,
              { timeout: 90000 }
            );
            d = res.data;
            break;
          } catch (err) {
            const code = err.response?.status;
            const isCloudflareEdge = code >= 520 && code <= 526;
            const isRetryable =
              !err.response || (code >= 500 && code < 600) || err.code === 'ECONNABORTED';
            if (!isRetryable || attempt >= 3) throw err;
            await new Promise(r => setTimeout(r, isCloudflareEdge ? 15000 : 3000 * attempt));
          }
        }

        if (batchNum === 1) totalFreeable = d.freeable_bytes || 0;
        totalFreed += d.bytes_freed || 0;
        totalPurged += d.files_purged || 0;
        totalFailed += d.files_failed_verification || 0;
        if (d.failed_details && d.failed_details.length) {
          allFailures = allFailures.concat(d.failed_details);
        }

        const pct = totalFreeable ? Math.round((totalFreed / totalFreeable) * 100) : 0;
        const mb = (totalFreed / (1024 * 1024)).toFixed(1);
        toast.loading(
          `Freed ${mb} MB so far — ${totalPurged} files done (${pct}%)`,
          { id: toastId }
        );

        if ((d.files_purged ?? 0) === 0 && (d.files_failed_verification ?? 0) === 0) break;
        if (!d.still_has_pending) break;
      }
      toast.dismiss(toastId);
      const freedMB = (totalFreed / (1024 * 1024)).toFixed(2);
      const msg =
        `Storage freed: ${freedMB} MB across ${totalPurged} files.` +
        (totalFailed > 0
          ? ` ${totalFailed} files could not be verified and were kept untouched (your data is still safe).`
          : ' All files verified safe before purging.');
      if (totalFailed > 0) {
        toast.warning(msg, { duration: 15000 });
      } else {
        toast.success(msg, { duration: 12000 });
      }
      // Refresh storage card
      fetchStorageUsage();
      return { totalFreed, totalPurged, totalFailed, allFailures };
    } catch (e) {
      toast.dismiss(toastId);
      const detail = e.response?.data?.detail || e.message || 'unknown error';
      const partial = totalPurged > 0
        ? ` Partial progress: ${totalPurged} files purged (${(totalFreed / (1024 * 1024)).toFixed(1)} MB) — click again to resume.`
        : '';
      toast.error(`Free Up Space stopped at batch ${batchNum}: ${detail}.${partial}`, { duration: 12000 });
      return null;
    }
  };

  // === Backfill File Sizes ===
  // Writes the missing `size` + `storage_type` fields directly onto each row
  // in the documents collection. After this, the list view shows
  // KB/MB + storage badge for EVERY file without doing a bulk lookup.
  // Same retry/CF-edge backoff as the other long-running admin loops.
  const runBackfillSizes = async () => {
    const toastId = toast.loading('Reading file sizes…');
    let totalUpdated = 0;
    let totalUnresolved = 0;
    let batchNum = 0;
    try {
      while (batchNum < 200) {
        batchNum++;
        let attempt = 0;
        let d = null;
        while (attempt < 3) {
          attempt++;
          try {
            toast.loading(
              attempt > 1
                ? `Retrying batch ${batchNum} (attempt ${attempt}/3)…`
                : `Reading sizes — batch ${batchNum}…`,
              { id: toastId }
            );
            const res = await api.post(
              '/documents/admin/backfill-document-sizes?limit=100',
              null,
              { timeout: 60000 }
            );
            d = res.data;
            break;
          } catch (err) {
            const code = err.response?.status;
            const isCloudflareEdge = code >= 520 && code <= 526;
            const isRetryable =
              !err.response || (code >= 500 && code < 600) || err.code === 'ECONNABORTED';
            if (!isRetryable || attempt >= 3) throw err;
            await new Promise(r => setTimeout(r, isCloudflareEdge ? 15000 : 3000 * attempt));
          }
        }
        totalUpdated += d.updated || 0;
        totalUnresolved += d.unresolved || 0;
        toast.loading(`Read ${totalUpdated} file sizes so far…`, { id: toastId });
        if (!d.still_has_pending || (d.processed || 0) === 0) break;
      }
      toast.dismiss(toastId);
      const msg =
        `File sizes updated for ${totalUpdated} documents.` +
        (totalUnresolved > 0 ? ` (${totalUnresolved} files could not be located.)` : '');
      toast.success(msg, { duration: 8000 });
      fetchData();
      return { totalUpdated, totalUnresolved };
    } catch (e) {
      toast.dismiss(toastId);
      const detail = e.response?.data?.detail || e.message || 'unknown error';
      toast.error(`Backfill stopped at batch ${batchNum}: ${detail}`, { duration: 10000 });
      return null;
    }
  };

  // Verification handlers
  const handleVerifyClick = (doc) => {
    setDocumentToVerify(doc);
    setVerifyRemarks('');
    setVerifyDialogOpen(true);
  };

  const handleVerifyDocument = async (status) => {
    if (!documentToVerify) return;
    try {
      setVerifying(true);
      await verifyDocument(documentToVerify.id, status, verifyRemarks);
      toast.success(`Document ${status} successfully`);
      setVerifyDialogOpen(false);
      setDocumentToVerify(null);
      setVerifyRemarks('');
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${status} document`);
    } finally {
      setVerifying(false);
    }
  };

  // Re-upload handlers
  const handleReuploadClick = (doc) => {
    setDocumentToReupload(doc);
    setReuploadFile(null);
    setReuploadDialogOpen(true);
  };

  const handleReuploadFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setReuploadFile(file);
    }
  };

  const handleReuploadSubmit = async () => {
    if (!documentToReupload || !reuploadFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      setUploading(true);
      // Delete old document and upload new one with same metadata
      await deleteDocument(documentToReupload.id);
      await uploadDocument(
        reuploadFile,
        documentToReupload.employee_id,
        documentToReupload.document_name,
        documentToReupload.document_type
      );
      toast.success('Document re-uploaded successfully');
      setReuploadDialogOpen(false);
      setDocumentToReupload(null);
      setReuploadFile(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to re-upload document');
    } finally {
      setUploading(false);
    }
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle size={12} /> Verified</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 gap-1"><XCircle size={12} /> Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 gap-1"><Clock size={12} /> Pending</Badge>;
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.name || emp?.employee_id || 'N/A';
  };

  const getDocumentTypeLabel = (type) => {
    const docType = documentTypes.find(d => d.value === type);
    return docType?.label || type;
  };

  // Get file extension from filename or URL
  const getFileExtension = (doc) => {
    const filename = doc.original_filename || doc.document_name || '';
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || '';
  };

  // Format a byte count as "456 KB" / "1.2 MB" / "2.4 GB" etc.
  // Mirrors what most file managers show — keeps the badge compact.
  const formatFileSize = (bytes) => {
    if (bytes == null || bytes === 0) return '';
    const KB = 1024, MB = KB * 1024, GB = MB * 1024;
    if (bytes < KB) return `${bytes} B`;
    if (bytes < MB) return `${(bytes / KB).toFixed(bytes < 10 * KB ? 1 : 0)} KB`;
    if (bytes < GB) return `${(bytes / MB).toFixed(bytes < 10 * MB ? 1 : 0)} MB`;
    return `${(bytes / GB).toFixed(2)} GB`;
  };

  // Get icon based on file extension (actual file type)
  const getFileTypeIcon = (doc) => {
    const ext = getFileExtension(doc);
    
    // PDF files
    if (ext === 'pdf') {
      return { icon: FileText, color: 'text-red-600', bg: 'bg-red-50', label: 'PDF' };
    }
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
      return { icon: FileImage, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Image' };
    }
    
    // Word documents
    if (['doc', 'docx'].includes(ext)) {
      return { icon: FileType, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Word' };
    }
    
    // Excel spreadsheets
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return { icon: FileSpreadsheet, color: 'text-green-600', bg: 'bg-green-50', label: 'Excel' };
    }
    
    // Text files
    if (['txt', 'rtf'].includes(ext)) {
      return { icon: File, color: 'text-slate-600', bg: 'bg-slate-50', label: 'Text' };
    }
    
    // Default - use document type category
    return getDocumentCategoryIcon(doc.document_type);
  };

  // Get icon based on document category (ID docs, certificates, etc.)
  const getDocumentCategoryIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'aadhar':
      case 'pan':
      case 'passport':
      case 'driving_license':
      case 'voter_id':
        return { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', label: 'ID' };
      case 'resume':
      case 'education':
      case 'experience':
        return { icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Doc' };
      case 'offer_letter':
      case 'relieving_letter':
        return { icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50', label: 'Letter' };
      default:
        return { icon: File, color: 'text-slate-600', bg: 'bg-slate-50', label: 'File' };
    }
  };

  // Helper function to get the proper view URL for a document
  const getDocumentViewUrl = (doc) => {
    if (!doc.document_url) return null;
    
    // If it's already an API path (new format), construct full URL
    if (doc.document_url.startsWith('/api/')) {
      return `${window.location.origin}${doc.document_url}`;
    }
    
    // If it's an old format with uploaded:// prefix, it's not viewable
    if (doc.document_url.startsWith('uploaded://')) {
      return null;
    }
    
    // If it's an external URL (http/https), return as-is
    if (doc.document_url.startsWith('http://') || doc.document_url.startsWith('https://')) {
      return doc.document_url;
    }
    
    // For any other format, try to use it as external URL
    return doc.document_url;
  };

  // Check if document needs re-upload (uploaded with old system)
  const needsReupload = (doc) => {
    if (!doc.document_url) return true;
    if (doc.document_url.startsWith('uploaded://')) return true;
    return false;
  };

  const canManageAllDocs = user?.role === 'admin' || user?.role === 'hr_manager' || user?.role === 'hr_executive' || hasDocOverviewAccess;

  // Filter documents based on search and filters
  const filteredDocuments = documents.filter(doc => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      doc.document_name?.toLowerCase().includes(searchLower) ||
      getEmployeeName(doc.employee_id).toLowerCase().includes(searchLower) ||
      getDocumentTypeLabel(doc.document_type).toLowerCase().includes(searchLower);
    
    // Document type filter
    const matchesDocType = filterDocType === 'all' || doc.document_type === filterDocType;
    
    // Employee filter
    const matchesEmployee = filterEmployeeId === 'all' || doc.employee_id === filterEmployeeId;
    
    // Verification status filter
    const matchesVerification = filterVerificationStatus === 'all' || 
      (doc.verification_status || 'pending') === filterVerificationStatus;
    
    return matchesSearch && matchesDocType && matchesEmployee && matchesVerification;
  });

  // Filter employees for dropdown search
  const filteredEmployees = employees.filter(emp => {
    if (!employeeSearchQuery) return true;
    const searchLower = employeeSearchQuery.toLowerCase();
    const empName = (emp.name || '').toLowerCase();
    const empId = (emp.employee_id || '').toLowerCase();
    return empName.includes(searchLower) || empId.includes(searchLower);
  });

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="document-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">
              {user?.role === 'employee' ? 'My Documents' : 'Document Management'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {user?.role === 'employee' ? 'Upload and manage your documents' : 'Manage employee documents and files'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canManageAllDocs && (
              <Button
                variant="outline"
                className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={async () => {
                  // Two-step UX: dry-run first so admin can see the impact
                  // (e.g. "1600 files will be migrated") before committing.
                  const dryRun = await runMigration(false);
                  if (!dryRun) return;
                  const pending = dryRun.remaining_before ?? dryRun.total_legacy_rows;
                  if (!pending) {
                    toast.success('Nothing to migrate — all files are already in GridFS.');
                    return;
                  }
                  const ok = window.confirm(
                    `GridFS migration ready:\n\n` +
                    `• ${pending} legacy rows pending on this environment\n` +
                    `• Processed in batches of 3 (auto-retry on network blips)\n` +
                    `• Legacy rows kept as backup; safe to re-run if it stops mid-way\n\n` +
                    `Proceed? Keep this tab open until the success toast appears.\n` +
                    `If a batch fails after 3 retries, click the button again — it resumes from where it stopped.`
                  );
                  if (!ok) return;
                  await runMigration(true);
                }}
                data-testid="migrate-to-gridfs-btn"
              >
                <Database size={16} />
                Fix Document Timeouts
              </Button>
            )}
            {canManageAllDocs && (
              <Button
                variant="outline"
                className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={async () => {
                  // Two-step UX: dry-run first to preview MB freeable, then
                  // confirm before any data is touched. Per-file 1KB read
                  // from GridFS guarantees verify-then-purge safety on the
                  // backend, but the user still gets a final go/no-go here.
                  const preview = await runFreeUpSpace(false);
                  if (!preview) return;
                  const freeableMB = ((preview.freeable_bytes || 0) / (1024 * 1024)).toFixed(2);
                  const freeableFiles = preview.freeable_files || 0;
                  if (!freeableFiles) {
                    toast.success('Nothing to free — no migrated legacy data found.');
                    return;
                  }
                  const ok = window.confirm(
                    `Free Up Space — Safe Preview\n\n` +
                    `• ${freeableMB} MB can be safely freed\n` +
                    `• ${freeableFiles} files have a verified GridFS copy\n\n` +
                    `How it works (your files are NEVER deleted):\n` +
                    `  1. For each file, we open the new GridFS copy and read the first 1 KB\n` +
                    `  2. Only if that read succeeds, we delete just the OLD slow copy\n` +
                    `  3. The metadata row (filename, employee, status) is kept\n` +
                    `  4. If any file fails verification, we skip it and report it — your data stays safe\n\n` +
                    `Proceed? Keep this tab open until the success toast appears.`
                  );
                  if (!ok) return;
                  await runFreeUpSpace(true);
                }}
                data-testid="free-up-space-btn"
              >
                <HardDrive size={16} />
                Free Up Space
              </Button>
            )}
            {canManageAllDocs && (
              <Button
                variant="outline"
                className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                onClick={runBackfillSizes}
                data-testid="backfill-sizes-btn"
              >
                <RefreshCw size={16} />
                Refresh File Sizes
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2" data-testid="upload-document-btn">
                  <Upload size={18} />
                  Upload Document
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="upload-document-dialog">
              <DialogHeader>
                <DialogTitle>{user?.role === 'employee' ? 'Upload My Document' : 'Upload Document'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="document-form">
                {canManageAllDocs && (
                  <div className="space-y-2">
                    <Label htmlFor="employee_id">Employee *</Label>
                    <Popover open={employeeComboOpen} onOpenChange={setEmployeeComboOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={employeeComboOpen}
                          className="w-full justify-between font-normal"
                          data-testid="employee-select"
                        >
                          {formData.employee_id
                            ? (() => {
                                const emp = employees.find(e => e.id === formData.employee_id);
                                return emp ? `${emp.name || emp.employee_id}${emp.employee_id ? ` (${emp.employee_id})` : ''}` : 'Select employee';
                              })()
                            : 'Select employee'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search employee..." data-testid="employee-search-input" />
                          <CommandList>
                            <CommandEmpty>No employee found.</CommandEmpty>
                            <CommandGroup>
                              {employees.map(emp => (
                                <CommandItem
                                  key={emp.id}
                                  value={`${emp.name || ''} ${emp.employee_id || ''}`}
                                  onSelect={() => {
                                    setFormData({ ...formData, employee_id: emp.id });
                                    setEmployeeComboOpen(false);
                                  }}
                                  data-testid={`employee-option-${emp.id}`}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.employee_id === emp.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {emp.name || emp.employee_id} {emp.employee_id ? `(${emp.employee_id})` : ''}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="document_type">Document Type *</Label>
                  <Select
                    value={formData.document_type}
                    onValueChange={(value) => setFormData({ ...formData, document_type: value })}
                    required
                  >
                    <SelectTrigger data-testid="document-type-select">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="document_name">Document Name *</Label>
                  <Input
                    id="document_name"
                    value={formData.document_name}
                    onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                    placeholder="e.g., Aadhar Card Front"
                    required
                    data-testid="document-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file">Upload File</Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-indigo-500 transition-colors">
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="file"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <Upload className="mx-auto h-8 w-8 text-slate-400" />
                      <p className="mt-2 text-sm text-slate-600">
                        {formData.file ? formData.file.name : 'Click to upload or drag and drop'}
                      </p>
                      <p className="text-xs text-slate-400">PDF, JPG, PNG, DOC up to 5MB. Images auto-compressed.</p>
                    </label>
                  </div>
                  {/* Compression feedback — shows up after the user picks a
                      large image so they SEE the savings before submitting. */}
                  {formData._compressionInfo?.did_compress && (
                    <div
                      className="mt-2 flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700"
                      data-testid="compression-indicator"
                    >
                      <CheckCircle size={14} />
                      <span>
                        Compressed{' '}
                        <span className="font-semibold">{fmtBytes(formData._compressionInfo.original_bytes)}</span>{' '}
                        → <span className="font-semibold">{fmtBytes(formData._compressionInfo.compressed_bytes)}</span>{' '}
                        <span className="text-emerald-600">
                          ({Math.round((1 - formData._compressionInfo.compressed_bytes / formData._compressionInfo.original_bytes) * 100)}% smaller)
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="document_url">Or Enter Document URL</Label>
                  <Input
                    id="document_url"
                    value={formData.file ? '' : formData.document_url}
                    onChange={(e) => setFormData({ ...formData, document_url: e.target.value, file: null })}
                    placeholder="https://..."
                    disabled={!!formData.file}
                    data-testid="document-url-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" 
                  disabled={uploading}
                  data-testid="submit-btn"
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Storage Usage Card — admin/HR only. Tells users at a glance how
            much of the MongoDB free tier is being eaten by uploads, with a
            GridFS vs Legacy split + progress bar that warns red past 80%. */}
        {storageUsage && canManageAllDocs && (
          <Card className="bg-white" data-testid="storage-usage-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <div className="rounded-lg bg-indigo-50 p-2">
                    <Database size={18} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Storage Used</p>
                    <p className="text-lg font-bold text-slate-900" data-testid="storage-total-used">
                      {formatFileSize(storageUsage.total_bytes)}
                      <span className="text-sm font-normal text-slate-500 ml-1">
                        / {formatFileSize(storageUsage.quota_bytes)}
                      </span>
                    </p>
                    <p className="text-xs text-slate-400">{storageUsage.total_files} files</p>
                  </div>
                </div>

                <div className="flex flex-col gap-1 min-w-[140px]">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">GridFS (fast)</p>
                  <p className="text-base font-semibold text-emerald-700" data-testid="storage-gridfs">
                    {formatFileSize(storageUsage.gridfs_bytes) || '0 B'}
                  </p>
                  <p className="text-xs text-slate-400">{storageUsage.gridfs_files} files</p>
                </div>

                <div className="flex flex-col gap-1 min-w-[140px]">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Legacy (slow)</p>
                  <p className={`text-base font-semibold ${storageUsage.legacy_files > 0 ? 'text-amber-700' : 'text-slate-500'}`} data-testid="storage-legacy">
                    {formatFileSize(storageUsage.legacy_bytes) || '0 B'}
                  </p>
                  <p className="text-xs text-slate-400">{storageUsage.legacy_files} files</p>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span className="font-semibold uppercase tracking-wide">Quota</span>
                    <span data-testid="storage-percent">{storageUsage.percent_used}% used</span>
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        storageUsage.percent_used >= 80
                          ? 'bg-rose-500'
                          : storageUsage.percent_used >= 60
                          ? 'bg-amber-500'
                          : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(100, storageUsage.percent_used)}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {formatFileSize(storageUsage.remaining_bytes)} remaining
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        {canManageAllDocs && (
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                {/* Search Input */}
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                
                {/* Employee Filter with Search */}
                <div className="w-48">
                  <Label className="text-xs text-slate-500 mb-1 block">Employee</Label>
                  <Select value={filterEmployeeId} onValueChange={(v) => {
                    setFilterEmployeeId(v);
                    setEmployeeSearchQuery('');
                  }}>
                    <SelectTrigger data-testid="employee-filter">
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 pb-2 sticky top-0 bg-white z-10">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder="Search employee..."
                            value={employeeSearchQuery}
                            onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                            className="pl-8 h-9"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <SelectItem value="all">All Employees</SelectItem>
                      {filteredEmployees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name || emp.employee_id} {emp.employee_id ? `(${emp.employee_id})` : ''}
                        </SelectItem>
                      ))}
                      {filteredEmployees.length === 0 && (
                        <div className="py-4 text-center text-sm text-slate-500">No employees found</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Document Type Filter */}
                <div className="w-40">
                  <Label className="text-xs text-slate-500 mb-1 block">Document Type</Label>
                  <Select value={filterDocType} onValueChange={setFilterDocType}>
                    <SelectTrigger data-testid="doctype-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {documentTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Verification Status Filter */}
                <div className="w-36">
                  <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                  <Select value={filterVerificationStatus} onValueChange={setFilterVerificationStatus}>
                    <SelectTrigger data-testid="verification-filter">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Refresh Button */}
                <Button variant="outline" onClick={fetchData} className="gap-2">
                  <RefreshCw size={16} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document List - Table View */}
        <Card className="bg-white">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderOpen size={20} className="text-indigo-600" />
              Documents ({filteredDocuments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredDocuments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FolderOpen className="h-16 w-16 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No documents found</h3>
                <p className="text-slate-500 mt-1">
                  {searchQuery || filterDocType !== 'all' || filterEmployeeId !== 'all' 
                    ? 'Try adjusting your search or filters' 
                    : 'Upload your first document to get started'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="documents-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Document</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Type</th>
                      {canManageAllDocs && (
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                      )}
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Uploaded</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.map((doc, index) => {
                      const { icon: Icon, color, bg, label } = getFileTypeIcon(doc);
                      const ext = getFileExtension(doc);
                      return (
                        <tr key={doc.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 ${bg} rounded-xl flex flex-col items-center justify-center flex-shrink-0`}>
                                <Icon className={color} size={22} />
                                {ext && (
                                  <span className={`text-[9px] font-bold uppercase mt-0.5 ${color}`}>
                                    {ext}
                                  </span>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{doc.document_name}</p>
                                {doc.original_filename && (
                                  <p className="text-xs text-slate-400 truncate max-w-[200px]">
                                    {doc.original_filename}
                                  </p>
                                )}
                                {/* Storage indicator — tells admin whether this doc is on the fast
                                    GridFS path or the slow legacy collection. Helps triage
                                    "Document failed to load" reports at a glance. */}
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  {doc.storage === 'gridfs' && (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5"
                                      title="Stored in GridFS — streams instantly, no timeouts"
                                      data-testid={`storage-badge-${doc.id}`}
                                    >
                                      <Database size={10} /> GridFS
                                    </span>
                                  )}
                                  {doc.storage === 'legacy' && (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
                                      title="Legacy base64 storage — may time out on large files. Click 'Fix Document Timeouts' to migrate."
                                      data-testid={`storage-badge-${doc.id}`}
                                    >
                                      <Database size={10} /> Legacy
                                    </span>
                                  )}
                                  {doc.storage === 'missing' && (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5"
                                      title="No file attached — re-upload needed"
                                      data-testid={`storage-badge-${doc.id}`}
                                    >
                                      <Database size={10} /> Missing
                                    </span>
                                  )}
                                  {/* On-disk size of the underlying file. `formatFileSize` outputs
                                      "456 KB", "1.2 MB", etc. Helps admins see at a glance which
                                      uploads are eating into their MongoDB free tier. */}
                                  {doc.file_size_bytes != null && doc.file_size_bytes > 0 && (
                                    <span
                                      className="inline-flex items-center text-[10px] font-medium tracking-wide text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-1.5 py-0.5"
                                      title={`File occupies ${doc.file_size_bytes.toLocaleString()} bytes of storage`}
                                      data-testid={`size-badge-${doc.id}`}
                                    >
                                      {formatFileSize(doc.file_size_bytes)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                              {getDocumentTypeLabel(doc.document_type)}
                            </Badge>
                          </td>
                          {canManageAllDocs && (
                            <td className="py-3 px-4">
                              <p className="text-sm text-slate-700">{getEmployeeName(doc.employee_id)}</p>
                            </td>
                          )}
                          <td className="py-3 px-4">
                            {getVerificationBadge(doc.verification_status)}
                            {doc.verification_remarks && (
                              <p className="text-xs text-slate-400 mt-1 max-w-[150px] truncate" title={doc.verification_remarks}>
                                {doc.verification_remarks}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-slate-500">
                              {new Date(doc.uploaded_at || doc.created_at).toLocaleDateString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric'
                              })}
                            </p>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              {getDocumentViewUrl(doc) ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(getDocumentViewUrl(doc), '_blank')}
                                  className="text-indigo-600 hover:bg-indigo-50"
                                  data-testid={`view-doc-${doc.id}`}
                                  title="View Document"
                                >
                                  <Eye size={16} />
                                </Button>
                              ) : needsReupload(doc) && canManageAllDocs ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReuploadClick(doc)}
                                  className="text-amber-600 hover:bg-amber-50"
                                  data-testid={`reupload-doc-${doc.id}`}
                                  title="Re-upload Document"
                                >
                                  <Upload size={16} />
                                </Button>
                              ) : needsReupload(doc) ? (
                                <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                                  Re-upload needed
                                </Badge>
                              ) : null}
                              {canManageAllDocs && (doc.verification_status || 'pending') !== 'verified' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleVerifyClick(doc)}
                                  className="text-emerald-600 hover:bg-emerald-50"
                                  data-testid={`verify-doc-${doc.id}`}
                                  title="Verify Document"
                                >
                                  <ShieldCheck size={16} />
                                </Button>
                              )}
                              {canManageAllDocs && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteClick(doc)}
                                  className="text-red-600 hover:bg-red-50"
                                  data-testid={`delete-doc-${doc.id}`}
                                  title="Delete Document"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verification Dialog */}
        <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-emerald-600 flex items-center gap-2">
                <ShieldCheck size={20} />
                Verify Document
              </DialogTitle>
              <DialogDescription>
                Review and verify "{documentToVerify?.document_name}"
              </DialogDescription>
            </DialogHeader>
            
            {/* Preview Link */}
            {documentToVerify && getDocumentViewUrl(documentToVerify) && (
              <div className="py-2">
                <Button 
                  variant="outline" 
                  onClick={() => window.open(getDocumentViewUrl(documentToVerify), '_blank')}
                  className="w-full gap-2"
                >
                  <Eye size={16} />
                  View Document
                </Button>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <Label>Remarks (Optional)</Label>
                <Textarea
                  placeholder="Add any remarks about this document..."
                  value={verifyRemarks}
                  onChange={(e) => setVerifyRemarks(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={() => setVerifyDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => handleVerifyDocument('rejected')}
                disabled={verifying}
                className="bg-red-600 hover:bg-red-700"
              >
                <XCircle size={16} className="mr-1" />
                Reject
              </Button>
              <Button 
                onClick={() => handleVerifyDocument('verified')}
                disabled={verifying}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle size={16} className="mr-1" />
                Verify
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Re-upload Dialog */}
        <Dialog open={reuploadDialogOpen} onOpenChange={setReuploadDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-amber-600 flex items-center gap-2">
                <Upload size={20} />
                Re-upload Document
              </DialogTitle>
              <DialogDescription>
                Upload a new file for "{documentToReupload?.document_name}". This will replace the old document.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 mb-1">Document Type</p>
                <p className="font-medium">{getDocumentTypeLabel(documentToReupload?.document_type)}</p>
              </div>
              
              <div>
                <Label>Select New File</Label>
                <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                  onClick={() => reuploadFileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={reuploadFileInputRef}
                    onChange={handleReuploadFileChange}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  <Upload className="mx-auto h-8 w-8 text-slate-400" />
                  <p className="mt-2 text-sm text-slate-600">
                    {reuploadFile ? reuploadFile.name : 'Click to select file'}
                  </p>
                  <p className="text-xs text-slate-400">PDF, JPG, PNG, DOC up to 10MB</p>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => {
                setReuploadDialogOpen(false);
                setReuploadFile(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleReuploadSubmit}
                disabled={uploading || !reuploadFile}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {uploading ? 'Uploading...' : 'Re-upload Document'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 size={20} />
                Delete Document
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{documentToDelete?.document_name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DocumentManagement;